export type ScheduleCadence = "daily" | "weekdays" | "weekly" | "monthly";

export const WEEKDAYS = [
  { value: "MO", label: "Monday" },
  { value: "TU", label: "Tuesday" },
  { value: "WE", label: "Wednesday" },
  { value: "TH", label: "Thursday" },
  { value: "FR", label: "Friday" },
  { value: "SA", label: "Saturday" },
  { value: "SU", label: "Sunday" },
] as const;

export type Weekday = (typeof WEEKDAYS)[number]["value"];

export type ScheduleDraft = {
  enabled: boolean;
  cadence: ScheduleCadence;
  time: string;
  weekday: Weekday;
  monthday: number;
  prompt: string;
};

export const EMPTY_SCHEDULE: ScheduleDraft = {
  enabled: false,
  cadence: "daily",
  time: "09:00",
  weekday: "MO",
  monthday: 1,
  prompt: "",
};

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const scheduleToRrule = (draft: ScheduleDraft): string => {
  const match = TIME.exec(draft.time);
  const hour = match ? Number(match[1]) : 9;
  const minute = match ? Number(match[2]) : 0;
  const clock = `BYHOUR=${hour};BYMINUTE=${minute};BYSECOND=0`;
  if (draft.cadence === "weekdays") {
    return `FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR;${clock}`;
  }
  if (draft.cadence === "weekly") {
    return `FREQ=WEEKLY;BYDAY=${draft.weekday};${clock}`;
  }
  if (draft.cadence === "monthly") {
    const day = Math.min(28, Math.max(1, draft.monthday));
    return `FREQ=MONTHLY;BYMONTHDAY=${day};${clock}`;
  }
  return `FREQ=DAILY;${clock}`;
};

const PICKER_KEYS = new Set([
  "FREQ",
  "BYHOUR",
  "BYMINUTE",
  "BYSECOND",
  "BYDAY",
  "BYMONTHDAY",
]);

export const rruleToSchedule = (
  rrule: string,
  prompt: string,
  enabled = true,
): ScheduleDraft | null => {
  const parts = Object.fromEntries(
    rrule
      .split(";")
      .filter(Boolean)
      .map((piece) => piece.split("=")),
  );
  if (Object.keys(parts).some((key) => !PICKER_KEYS.has(key))) return null;
  if (parts.BYSECOND && parts.BYSECOND !== "0") return null;
  if (parts.BYDAY && parts.BYMONTHDAY) return null;
  const freq = parts.FREQ ?? null;
  const hour = parts.BYHOUR ?? null;
  const minute = parts.BYMINUTE ?? "0";
  const byday = parts.BYDAY ?? null;
  const monthday = parts.BYMONTHDAY ?? null;
  if (!hour || hour.includes(",") || minute.includes(",")) return null;
  const time = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}`;
  if (!TIME.test(time)) return null;
  const base = {
    enabled,
    time,
    prompt,
    weekday: "MO" as Weekday,
    monthday: 1,
  };
  if (freq === "DAILY" && !byday && !monthday) {
    return { ...base, cadence: "daily" };
  }
  if (freq === "WEEKLY" && byday === "MO,TU,WE,TH,FR") {
    return { ...base, cadence: "weekdays" };
  }
  if (freq === "WEEKLY" && WEEKDAYS.some((day) => day.value === byday)) {
    return { ...base, cadence: "weekly", weekday: byday as Weekday };
  }
  if (
    freq === "MONTHLY" &&
    monthday &&
    Number(monthday) >= 1 &&
    Number(monthday) <= 28
  ) {
    return { ...base, cadence: "monthly", monthday: Number(monthday) };
  }
  return null;
};
